'use client';

import { FC, useContext, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Text,
  Flex,
  Button,
  ButtonGroup,
  Input,
  Alert,
} from '@/_components';
import { RenameDocumentDialogProps } from './RenameDocumentDialog.types';
import { renameItem } from '@/_api/client-documents';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { sanitizeName, validateName } from '@/_utils/sanitize';
import { getExtensionOfFilename } from '@/_utils/files';
import styles from './RenameDocumentDialog.module.css';

export const RenameDocumentDialog: FC<RenameDocumentDialogProps> = ({
  item,
  onClose,
}) => {
  const { addToast } = useContext(ToastContext);
  const { isAdvisor, token } = useContext(UserContext);
  const { reloadFiles, reloadDocuments, companyId, openDirectory } =
    useContext(DocumentsContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const validation = useMemo(() => validateName(name), [name]);

  const originalExtension: string = getExtensionOfFilename(item?.name);

  const isFolder: boolean = item?.type === 'folder';

  useEffect(() => {
    if (item?.name && !isFolder) {
      const fileName: string =
        item.name.substring(0, item.name.lastIndexOf('.')) || item.name;
      setName(fileName);
    } else {
      setName(item?.name || '');
    }
  }, [item?.name, isFolder]);

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setName(event.target.value);

  const onRename = async () => {
    setLoading(true);

    try {
      await renameItem({
        companyId: companyId!,
        itemName: item?.name!,
        newName: sanitizeName(name, item?.name),
        path: openDirectory,
        extractedDocumentId: item?.extracted_document_id,
        token,
      });

      if (item?.type === 'folder') {
        reloadDocuments();
      } else {
        reloadFiles();
      }
    } catch (error) {
      addToast('error', 'Failed to update document or folder', { error });
    } finally {
      setName('');
      onClose();
      setLoading(false);
    }
  };

  return (
    <Dialog isOpen={!!item} onClose={onClose} title="Rename">
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          {item?.type === 'folder' && isAdvisor && (
            <Alert type="info">
              Add <b>STAFF ONLY</b> in the folder name if you want the folder to
              be hidden from the client&apos;s view.
            </Alert>
          )}
          <Flex gap={4} yAlign="end">
            <Input
              value={name}
              label="New Name"
              autoFocus={true}
              autoComplete="new-password"
              onChange={onNameChange}
              placeholder={name}
              helperText={!validation.isValid ? validation.error : undefined}
              helperTextColor={!validation.isValid ? 'critical' : undefined}
            />
            {originalExtension && !isFolder && (
              <Flex className={styles.extensionText} isCentered={true}>
                <Text type="small">.{originalExtension}</Text>
              </Flex>
            )}
          </Flex>
          <ButtonGroup align="right">
            <Button value="Cancel" intent="secondary" onClick={onClose} />
            <Button
              value="Rename"
              intent="primary"
              isLoading={isLoading}
              onClick={onRename}
              isDisabled={isLoading || !validation.isValid}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
